import React, { MouseEvent, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid } from '@mui/material';
import {
  BigKey,
  Card,
  CardTitle,
} from 'spw-programming-projects/program-summary/components/styles';
import {
  AlignEndColumn,
  AlignStartColumn,
} from 'spw-shared/globalComponents/grid';
import {
  AsteriskContainer,
  BoldValue,
  SeatTypeMixCategoryName,
  SeatTypeMixColumn,
} from 'spw-programming-projects/program-summary/macro-single-page-summary/styles';
import {
  AlignEndColumnRightPadding,
  HeaderColumn,
  SeatMixBox,
} from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/styles';
import {
  selectKeyData,
  selectSelectedLevelsExpanded,
} from 'spw-redux/modules/program-summary/esa/selectors';
import {
  ISpaceBreakDown,
  UpdateSpacesByTypeParams,
} from 'spw-redux/modules/program-summary/types';
import { spaceFactorMap as rawSpaceFactorMap } from 'spw-programming-projects/program-summary/macro-single-page-summary/utils';
import { SmallBubbleIcon } from 'spw-components/charts/styles';
import {
  asRatioRoundNumber,
  getCustomSpaceColor,
  getIsLockerOrCabinet,
  numberWithCommas,
} from 'spw-shared/utils';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { SpaceIcon } from 'spw-projects/components/space-type-icons';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { PSCardGroupHeader, Row } from 'spw-shared/globalComponents';
import { ProgramSummaryCardsContext } from 'spw-esa-projects/program-summary/summary-cards';
import { selectCurrentSpaceTypeColors } from 'spw-redux/modules/space_types/selectors';
import { CardLegend } from 'spw-components/project-details-tabs/program-summary/shared/styles';
import { AvgSeatCountColumn } from './styles';
import AvgCardPopover from './avg-card-popover';

const SpaceBreakdown = ({
  spaceFactorMap,
  spaceTypeId,
  breakdown,
  projectColors,
  projectSpaceTypeCategories,
  viewOnly,
  projectSpaceTypes,
  onClickSpaceIcon,
  onUpdateAvgSeatCount,
  clientFacingNamesAreVisible,
}: {
  spaceFactorMap: Record<
    string,
    { title: string; longTitle?: string; defaultCategory?: string; color: any }
  >;
  spaceTypeId: number;
  breakdown: ISpaceBreakDown;
  projectColors: IProjectColors;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  viewOnly: boolean;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  onClickSpaceIcon: (e: Element) => void;
  onUpdateAvgSeatCount?: (payload: UpdateSpacesByTypeParams) => void;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { allSelectedFloorIds } = useSelector(selectSelectedLevelsExpanded);
  const { isRenderingForExport } = useContext(ProgramSummaryCardsContext);
  const currentSpaceTypeColors = useSelector(selectCurrentSpaceTypeColors);

  let factors = spaceFactorMap[breakdown.spaceType];
  if (!factors) {
    const color = getCustomSpaceColor(
      breakdown.category,
      breakdown.spaceType,
      projectColors
    );
    factors = {
      title: breakdown.name,
      color,
    };
  }

  const projectSpaceType = projectSpaceTypes?.[spaceTypeId];
  const wasSpaceTypeReassigned =
    factors.defaultCategory !==
    projectSpaceType?.category.defaultSpaceTypeCategory.category;

  if (wasSpaceTypeReassigned) {
    factors.color = projectColors[breakdown.category];
  }

  const topLevelProjectSpaceTypeCategory = projectSpaceTypeCategories.find(
    (pstc) =>
      pstc.defaultSpaceTypeCategory.category === breakdown.category ||
      pstc.subcategories.find(
        (subcategory) =>
          subcategory.defaultSpaceTypeCategory.category ===
          (breakdown.category as Category | Subcategory)
      )
  );
  const projectSpaceTypeCategory = projectSpaceTypes[spaceTypeId]?.category;

  const isLockerOrCabinet = getIsLockerOrCabinet(breakdown?.spaceType);

  const handleAvgSeatcountClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleSaveAvgSeats = useCallback(
    (seatCount: number, callback: () => void) => {
      onUpdateAvgSeatCount({
        spaceTypeId: breakdown.spaceTypeId,
        seatCount,
        floorIds: allSelectedFloorIds,
        callback,
      });
    },
    [onUpdateAvgSeatCount, breakdown.spaceTypeId, allSelectedFloorIds]
  );

  const disableAvgSeatCountButton = isLockerOrCabinet || viewOnly;

  const name = clientFacingNamesAreVisible
    ? projectSpaceType?.clientFacingName || breakdown.name
    : breakdown.name;

  return (
    <>
      <Grid container>
        <SeatTypeMixCategoryName item xs={10}>
          {name}
        </SeatTypeMixCategoryName>
      </Grid>
      <Grid container data-testid={`space_type-${breakdown.spaceTypeId}`}>
        <Grid item xs={4}>
          <Box display="flex" flexDirection="row" flexWrap="nowrap">
            <Box>
              <SpaceIcon
                disabled={viewOnly}
                spaceTypeId={breakdown.spaceTypeId}
                topLevelProjectSpaceTypeCategoryId={
                  topLevelProjectSpaceTypeCategory?.id
                }
                projectSpaceTypeCategoryId={projectSpaceTypeCategory?.id}
                projectSpaceTypeId={
                  projectSpaceTypes[breakdown.spaceTypeId]?.id
                }
                onClick={onClickSpaceIcon}
                iconName={
                  projectSpaceTypes[breakdown.spaceTypeId]?.icon ||
                  breakdown.icon
                }
                defaultIconName={breakdown.icon}
                name={breakdown.name}
              />
            </Box>
            <Box display="flex" alignItems="center">
              <SmallBubbleIcon
                color={currentSpaceTypeColors[projectSpaceType.id].color}
              />
            </Box>
          </Box>
        </Grid>
        <SeatTypeMixColumn item xs={2} className="padding-right-16">
          <SeatMixBox
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <Box>
              <BoldValue data-testid="quantity">
                {breakdown.quantity
                  ? numberWithCommas(Math.round(breakdown.quantity))
                  : 0}
                {isLockerOrCabinet ? (
                  <AsteriskContainer>*</AsteriskContainer>
                ) : null}
              </BoldValue>
            </Box>
            <Box>Quantity</Box>
          </SeatMixBox>
        </SeatTypeMixColumn>
        <SeatTypeMixColumn item xs={2} className="padding-right-16">
          <SeatMixBox
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <Box>
              <BoldValue data-testid="seats">
                {breakdown.seats
                  ? numberWithCommas(Math.round(breakdown.seats))
                  : 0}
              </BoldValue>
            </Box>
            <Box>Seats</Box>
          </SeatMixBox>
        </SeatTypeMixColumn>

        <SeatTypeMixColumn
          item
          xs={2}
          className="padding-right-16 border-right"
        >
          <SeatMixBox
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <AvgSeatCountColumn
              onClick={
                !disableAvgSeatCountButton && !isRenderingForExport
                  ? handleAvgSeatcountClick
                  : undefined
              }
              className={anchorEl ? 'selected' : ''}
              $disableHoverState={
                disableAvgSeatCountButton || isRenderingForExport
              }
            >
              <BoldValue data-testid="average">
                {breakdown.seatsCapacity
                  ? numberWithCommas(breakdown.seatsCapacity.toFixed(2))
                  : 0}
              </BoldValue>
              <div className="label">Avg Seats</div>
            </AvgSeatCountColumn>
          </SeatMixBox>
        </SeatTypeMixColumn>
        <SeatTypeMixColumn item xs={2}>
          <SeatMixBox
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexDirection="column"
          >
            <Box minWidth="max-content">
              <BoldValue data-testid="ratio">
                {Math.round(breakdown.ratio)
                  ? asRatioRoundNumber(Math.round(breakdown.ratio))
                  : '1: --'}
              </BoldValue>
            </Box>
            <Box>Ratio</Box>
          </SeatMixBox>
        </SeatTypeMixColumn>
      </Grid>
      {!!anchorEl && (
        <AvgCardPopover
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          subject="Seats"
          spaceTypeName={name}
          texts={[
            'Changing this value updates the Seat Count for all of these spaces that are located on the floors selected in this Program Summary.',
            'To modify which floors are affected by this change, select floors from the dropdown at the top of this page before saving this updated value.',
          ]}
          onSave={handleSaveAvgSeats}
          max={5000}
          min={0}
        />
      )}
    </>
  );
};

export const SpaceRatioCard = ({
  title,
  onClickSpaceIcon,
  spacesBreakdown = [],
  projectColors,
  projectSpaceTypeCategories,
  projectSpaceTypes,
  viewOnly,
  spaceTypeCategory,
  onUpdateAvgSeatCount,
  clientFacingNamesAreVisible,
}: {
  title: string;
  onClickSpaceIcon?: (e: Element) => void;
  spacesBreakdown: ISpaceBreakDown[];
  projectColors: IProjectColors;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  spaceTypeCategory: IProjectSpaceTypeCategory;
  onUpdateAvgSeatCount?: (payload: UpdateSpacesByTypeParams) => void;
  clientFacingNamesAreVisible: boolean;
}) => {
  const keyData = useSelector(selectKeyData);

  const totals = spacesBreakdown.reduce(
    (
      accumulator: {
        totalQuantity: number;
        totalSeats: number;
      },
      categoryBreakdown: ISpaceBreakDown
    ) => {
      if (!getIsLockerOrCabinet(categoryBreakdown.spaceType)) {
        accumulator.totalQuantity += categoryBreakdown.quantity;
        accumulator.totalSeats += categoryBreakdown.seats;
      }
      return accumulator;
    },
    { totalQuantity: 0, totalSeats: 0 }
  );

  const thereAreCabinetsOrLockers = spacesBreakdown.some((s) =>
    getIsLockerOrCabinet(s.spaceType)
  );

  const spaceFactorMap = rawSpaceFactorMap(projectColors);

  return (
    <Card>
      <Box>
        <CardTitle>{title}</CardTitle>
        <Grid container>
          <HeaderColumn xs={4}>
            <AlignStartColumn>
              <BigKey style={{ marginTop: '16px' }}>Totals</BigKey>
            </AlignStartColumn>
          </HeaderColumn>
          <HeaderColumn item xs={2}>
            <AlignEndColumnRightPadding>
              <BoldValue data-testid="total_quantity">
                {totals.totalQuantity > 0
                  ? numberWithCommas(Math.round(totals.totalQuantity))
                  : totals.totalQuantity}
              </BoldValue>
              <BigKey>Quantity</BigKey>
            </AlignEndColumnRightPadding>
          </HeaderColumn>
          <HeaderColumn item xs={2}>
            <AlignEndColumnRightPadding>
              <BoldValue data-testid="total_seats">
                {totals.totalSeats > 0
                  ? numberWithCommas(Math.round(totals.totalSeats))
                  : totals.totalSeats}
              </BoldValue>
              <BigKey>Seats</BigKey>
            </AlignEndColumnRightPadding>
          </HeaderColumn>
          <HeaderColumn item xs={2} className="border-right" />
          <HeaderColumn item xs={2}>
            <AlignEndColumn>
              <BoldValue data-testid="total_quantity">
                1:{' '}
                {numberWithCommas(keyData.workseats / totals.totalQuantity) ||
                  NO_DATA_TO_SHOW}
              </BoldValue>
              <BigKey>Ratio</BigKey>
            </AlignEndColumn>
          </HeaderColumn>
        </Grid>
        <PSCardGroupHeader>
          <Row>
            <span>{spaceTypeCategory.name.toUpperCase()}</span>
          </Row>
        </PSCardGroupHeader>
        {spacesBreakdown.map((breakdown: ISpaceBreakDown) => {
          return (
            <SpaceBreakdown
              key={`workspace-seat-mix-${breakdown.spaceTypeId}`}
              spaceFactorMap={spaceFactorMap}
              breakdown={breakdown}
              projectColors={projectColors}
              spaceTypeId={breakdown.spaceTypeId}
              projectSpaceTypeCategories={projectSpaceTypeCategories}
              viewOnly={viewOnly}
              projectSpaceTypes={projectSpaceTypes}
              onClickSpaceIcon={onClickSpaceIcon}
              onUpdateAvgSeatCount={onUpdateAvgSeatCount}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          );
        })}
      </Box>
      {thereAreCabinetsOrLockers && (
        <Box mt={1}>
          <CardLegend>* Not included in Total Quantity</CardLegend>
        </Box>
      )}
    </Card>
  );
};
